import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Plane, ShieldAlert, Loader2, X, ArrowRightLeft, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger
} from '@/components/ui/tooltip';
import { format } from 'date-fns';

/**
 * CaptainBanner — displays current captain on a feedback item and provides
 * claim / release / transfer / override / set-as-project-default actions.
 *
 * Two coexisting layers (FB-69fcc224):
 *   L1 — Card captain: SOFT lock (informational banner; does NOT block other UI on this page)
 *   L2 — Repo lock:    HARD mutex (per ProjectRegistry.id; only one pilot per repo;
 *                      Take Controls greyed for non-captains; only CiC can override)
 *
 * Props:
 *   feedback              — the Feedback record (id, project_id, captain*, captain_history)
 *   compact               — if true, render a small inline pill instead of a full banner
 *   onChange              — optional callback fired after a successful captain mutation
 */
export default function CaptainBanner({ feedback, compact, onChange }) {
  const qc = useQueryClient();

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me()
  });

  const [transferOpen, setTransferOpen] = useState(false);
  const [transferEmail, setTransferEmail] = useState('');
  const [setDefaultOpen, setSetDefaultOpen] = useState(false);
  const [defaultEmail, setDefaultEmail] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);

  const isAdmin = user?.role === 'admin';
  const callerEmail = user?.email || '';

  /* Fetch effective captain + repo-lock state */
  const { data: state, isLoading: loadingState } = useQuery({
    queryKey: ['captain', feedback?.id],
    queryFn: async () => {
      const resp = await base44.functions.invoke('captainControl', {
        action: 'read',
        feedback_id: feedback.id
      });
      return resp?.data || resp;
    },
    enabled: !!feedback?.id && !!user,
    refetchInterval: 30000 /* 30s lightweight refresh so soft-locks stay current */
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['captain', feedback?.id] });
    qc.invalidateQueries({ queryKey: ['feedback'] });
    if (onChange) onChange();
  };

  const claim = useMutation({
    mutationFn: async () => {
      const resp = await base44.functions.invoke('captainControl', {
        action: 'claim',
        scope: 'item',
        feedback_id: feedback.id
      });
      return resp?.data || resp;
    },
    onSuccess: invalidate
  });

  const release = useMutation({
    mutationFn: async (successor) => {
      const resp = await base44.functions.invoke('captainControl', {
        action: 'release',
        scope: 'item',
        feedback_id: feedback.id,
        successor_email: successor || ''
      });
      return resp?.data || resp;
    },
    onSuccess: () => {
      setTransferOpen(false);
      setTransferEmail('');
      invalidate();
    }
  });

  const override = useMutation({
    mutationFn: async () => {
      const resp = await base44.functions.invoke('captainControl', {
        action: 'override',
        feedback_id: feedback.id,
        reason: 'admin_override'
      });
      return resp?.data || resp;
    },
    onSuccess: invalidate
  });

  const overrideRepo = useMutation({
    mutationFn: async () => {
      const resp = await base44.functions.invoke('captainControl', {
        action: 'override_repo',
        feedback_id: feedback.id,
        reason: 'cic_repo_override'
      });
      return resp?.data || resp;
    },
    onSuccess: invalidate
  });

  const setProjectDefault = useMutation({
    mutationFn: async (emailToSet) => {
      const resp = await base44.functions.invoke('captainControl', {
        action: 'claim',
        scope: 'project',
        project_id: feedback.project_id,
        captain_email: emailToSet
      });
      return resp?.data || resp;
    },
    onSuccess: () => {
      setSetDefaultOpen(false);
      setDefaultEmail('');
      invalidate();
    }
  });

  if (!feedback?.id || loadingState || !state) {
    if (compact) return null;
    return (
      <div className="flex items-center gap-2 px-3 py-2 text-xs text-slate-400 bg-slate-900/40 border border-slate-800 rounded-md">
        <Loader2 className="w-3 h-3 animate-spin" /> Loading captain status…
      </div>
    );
  }

  const eff = state.effective_captain;
  const source = state.source; /* 'item' | 'project' | 'none' */
  const itemOverride = state.item_captain_override;
  const isMyPlane = !!eff && eff === callerEmail;

  /* Repo-lock derived state (FB-69fcc224) */
  const repoCaptain = state.repo_active_captain || null;
  const repoLabel = state.repo_lock_label || '';
  const repoLockedCardId = state.repo_active_captain_locked_card_id || '';
  const callerHoldsRepo = !!state.caller?.holds_repo_lock;
  const callerIsCiC = !!state.caller?.is_cic;
  /* Lockout: someone else holds the repo lock and it's not this card.
     Lock is reflected on every card in the repo *except* the one that anchored it
     (where the captain is actively working). */
  const repoLockedByOther = !!repoCaptain && !callerHoldsRepo;

  /* ── Compact pill (used in kanban cards / lists) ─────── */
  if (compact) {
    /* Show repo-lock pill if locked by other (overrides card-level pill) */
    if (repoLockedByOther) {
      const initials = (repoCaptain.split('@')[0] || repoCaptain).slice(0, 2).toUpperCase();
      return (
        <Badge
          title={repoCaptain + ' is captaining ' + (repoLabel || 'this repo')}
          className="text-[10px] px-1.5 py-0 font-medium border bg-rose-500/20 text-rose-300 border-rose-500/40">
          <Lock className="w-2.5 h-2.5 mr-0.5" />{initials}
        </Badge>
      );
    }
    if (!eff) return null;
    const initials = (eff.split('@')[0] || eff).slice(0, 2).toUpperCase();
    const tone = source === 'project'
      ? 'bg-slate-700/60 text-slate-300 border-slate-600'
      : (itemOverride ? 'bg-amber-500/20 text-amber-300 border-amber-500/40' : 'bg-sky-500/20 text-sky-300 border-sky-500/40');
    return (
      <Badge
        title={eff + (source === 'project' ? ' (project default)' : (itemOverride ? ' (admin override)' : ' has the plane'))}
        className={`text-[10px] px-1.5 py-0 font-medium border ${tone}`}>
        <Plane className="w-2.5 h-2.5 mr-0.5" />{initials}
      </Badge>
    );
  }

  /* ── Repo-locked-by-other banner (HARD mutex view) ─────
     Take precedence over the card-level banner when another pilot holds the repo lock.
     Caller can still read the card; Take Controls is disabled. CiC sees Override Repo. */
  if (repoLockedByOther) {
    const subtitleParts = [];
    if (state.repo_active_captain_since) {
      subtitleParts.push('Locked since ' + format(new Date(state.repo_active_captain_since), 'MMM d, h:mm a'));
    }
    if (repoLockedCardId && repoLockedCardId !== feedback.id) {
      subtitleParts.push('working on FB-' + repoLockedCardId.slice(-6));
    }
    subtitleParts.push('Take Controls disabled until release or 2h auto-expiry');
    const subtitle = subtitleParts.join(' · ');

    return (
      <div className="flex items-start gap-3 px-3 py-2 border rounded-md bg-rose-900/30 border-rose-700/50 text-rose-100">
        <div className="shrink-0 mt-0.5">
          <Lock className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium">
            {repoCaptain} is captaining {repoLabel || 'this repo'}
          </div>
          <div className="text-[11px] opacity-80 mt-0.5">{subtitle}</div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          {/* Disabled Take Controls with explanatory tooltip */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0}>
                  <Button size="sm" variant="outline" className="h-7 text-xs opacity-50 cursor-not-allowed" disabled>
                    Take controls
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-xs text-xs">
                {repoCaptain} has the repo lock on {repoLabel || 'this project'}. Try a card in a different repo, or wait for release.
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* CiC-only repo override */}
          {callerIsCiC && (
            <Button size="sm" variant="outline"
              className="h-7 text-xs border-rose-600 text-rose-200 hover:bg-rose-600/20"
              onClick={() => overrideRepo.mutate()}
              disabled={overrideRepo.isPending}>
              <ShieldAlert className="w-3 h-3 mr-1" />
              {overrideRepo.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Override repo lock'}
            </Button>
          )}

          {Array.isArray(feedback.captain_history) && feedback.captain_history.length > 0 && (
            <Button size="sm" variant="ghost" className="h-7 text-xs opacity-70 hover:opacity-100"
              onClick={() => setHistoryOpen(true)}>
              History
            </Button>
          )}
        </div>

        {/* History dialog (reuse same component below) */}
        <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Captain history</DialogTitle>
              <DialogDescription>Most recent transfers first.</DialogDescription>
            </DialogHeader>
            <div className="max-h-[400px] overflow-y-auto space-y-2 py-2">
              {(feedback.captain_history || []).slice().reverse().map((h, i) => (
                <div key={i} className="text-xs p-2 rounded bg-slate-900/60 border border-slate-800">
                  <div className="font-medium text-slate-200">
                    {h.action || 'transfer'}
                    {h.captain && <> — {h.captain}</>}
                    {h.override_by && <span className="ml-2 text-amber-400">(override)</span>}
                  </div>
                  <div className="text-slate-400 mt-1">
                    {h.taken_at && <>Taken: {format(new Date(h.taken_at), 'MMM d, h:mm a')} </>}
                    {h.released_at && <>· Released: {format(new Date(h.released_at), 'MMM d, h:mm a')} </>}
                    {h.successor && <>· → {h.successor} </>}
                    {h.reason && <>· {h.reason}</>}
                  </div>
                </div>
              ))}
              {(!feedback.captain_history || feedback.captain_history.length === 0) && (
                <div className="text-xs text-slate-500 text-center py-4">No history yet.</div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setHistoryOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  /* ── Full banner (existing card-level behavior) ──────── */
  let bannerTone = 'bg-slate-900/40 border-slate-800 text-slate-300';
  let title = 'No captain on this card';
  let subtitle = 'Anyone on the team can take controls.';

  if (eff) {
    if (isMyPlane) {
      bannerTone = 'bg-emerald-900/30 border-emerald-700/50 text-emerald-200';
      title = 'You have the plane';
      subtitle = source === 'project'
        ? 'Inherited from project default.'
        : (itemOverride ? 'You took controls (admin override).' : 'You took controls.');
      /* If the caller holds the repo lock too, surface that for clarity */
      if (callerHoldsRepo && repoLabel) {
        subtitle += ' You are captaining ' + repoLabel + '.';
      }
    } else if (source === 'project') {
      bannerTone = 'bg-slate-800/50 border-slate-700 text-slate-200';
      title = eff + ' has the plane';
      subtitle = 'Inherited from project default. Item-level claim will override.';
    } else if (itemOverride) {
      bannerTone = 'bg-amber-900/30 border-amber-700/50 text-amber-100';
      title = eff + ' has the plane (admin override)';
      subtitle = 'Took controls via Commander override.';
    } else {
      bannerTone = 'bg-sky-900/30 border-sky-700/50 text-sky-100';
      title = eff + ' has the plane';
      subtitle = state.item_captain_since
        ? 'Since ' + format(new Date(state.item_captain_since), 'MMM d, h:mm a')
        : '';
    }
  }

  return (
    <div className={`flex items-start gap-3 px-3 py-2 border rounded-md ${bannerTone}`}>
      <div className="shrink-0 mt-0.5">
        {eff ? <Plane className="w-4 h-4" /> : <Plane className="w-4 h-4 opacity-40" />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-medium">{title}</div>
        {subtitle && <div className="text-[11px] opacity-80 mt-0.5">{subtitle}</div>}
      </div>
      <div className="flex items-center gap-1.5 shrink-0">
        {/* No effective captain → anyone can claim (will auto-acquire repo lock) */}
        {!eff && (
          <Button size="sm" variant="outline" className="h-7 text-xs"
            onClick={() => claim.mutate()} disabled={claim.isPending}>
            {claim.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Take controls'}
          </Button>
        )}

        {/* I'm captain → release or transfer */}
        {isMyPlane && (
          <>
            <Button size="sm" variant="outline" className="h-7 text-xs"
              onClick={() => setTransferOpen(true)} disabled={release.isPending}>
              <ArrowRightLeft className="w-3 h-3 mr-1" /> Transfer
            </Button>
            <Button size="sm" variant="outline" className="h-7 text-xs"
              onClick={() => release.mutate('')} disabled={release.isPending}>
              {release.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : <><X className="w-3 h-3 mr-1" /> Release</>}
            </Button>
          </>
        )}

        {/* Someone else has it (item-level), I'm admin → override */}
        {eff && !isMyPlane && source === 'item' && isAdmin && (
          <Button size="sm" variant="outline" className="h-7 text-xs border-amber-600 text-amber-300 hover:bg-amber-600/20"
            onClick={() => override.mutate()} disabled={override.isPending}>
            <ShieldAlert className="w-3 h-3 mr-1" />
            {override.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Override'}
          </Button>
        )}

        {/* Someone else has it (project default), I'm admin → claim item-level (overrides project) */}
        {eff && !isMyPlane && source === 'project' && (
          <Button size="sm" variant="outline" className="h-7 text-xs"
            onClick={() => claim.mutate()} disabled={claim.isPending}>
            {claim.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Take controls'}
          </Button>
        )}

        {/* Admin extras: set as project default + history */}
        {isAdmin && feedback.project_id && (
          <Button size="sm" variant="ghost" className="h-7 text-xs opacity-70 hover:opacity-100"
            onClick={() => setSetDefaultOpen(true)}>
            Set project default
          </Button>
        )}
        {Array.isArray(feedback.captain_history) && feedback.captain_history.length > 0 && (
          <Button size="sm" variant="ghost" className="h-7 text-xs opacity-70 hover:opacity-100"
            onClick={() => setHistoryOpen(true)}>
            History
          </Button>
        )}
      </div>

      {/* ── Transfer dialog ── */}
      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transfer the plane</DialogTitle>
            <DialogDescription>Hand controls to a teammate. They'll see "you have the plane" on this item.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="successor-email">Teammate email</Label>
            <Input id="successor-email" type="email" placeholder="alexis@..." value={transferEmail} onChange={e => setTransferEmail(e.target.value)} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferOpen(false)}>Cancel</Button>
            <Button onClick={() => release.mutate(transferEmail)} disabled={!transferEmail || release.isPending}>
              {release.isPending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              Transfer controls
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Set project default dialog (admin) ── */}
      <Dialog open={setDefaultOpen} onOpenChange={setSetDefaultOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Set project default captain</DialogTitle>
            <DialogDescription>
              All feedback in this project without an item-level captain will inherit this default.
              Item-level claims always override the project default.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-2">
            <Label htmlFor="default-email">Captain email</Label>
            <Input id="default-email" type="email"
              placeholder={callerEmail}
              value={defaultEmail}
              onChange={e => setDefaultEmail(e.target.value)} />
            <p className="text-[11px] text-slate-400">Leave blank to set yourself as default.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSetDefaultOpen(false)}>Cancel</Button>
            <Button onClick={() => setProjectDefault.mutate(defaultEmail || callerEmail)} disabled={setProjectDefault.isPending}>
              {setProjectDefault.isPending ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
              Set as project default
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── History dialog ── */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Captain history</DialogTitle>
            <DialogDescription>Most recent transfers first.</DialogDescription>
          </DialogHeader>
          <div className="max-h-[400px] overflow-y-auto space-y-2 py-2">
            {(feedback.captain_history || []).slice().reverse().map((h, i) => (
              <div key={i} className="text-xs p-2 rounded bg-slate-900/60 border border-slate-800">
                <div className="font-medium text-slate-200">
                  {h.action || 'transfer'}
                  {h.captain && <> — {h.captain}</>}
                  {h.override_by && <span className="ml-2 text-amber-400">(override)</span>}
                </div>
                <div className="text-slate-400 mt-1">
                  {h.taken_at && <>Taken: {format(new Date(h.taken_at), 'MMM d, h:mm a')} </>}
                  {h.released_at && <>· Released: {format(new Date(h.released_at), 'MMM d, h:mm a')} </>}
                  {h.successor && <>· → {h.successor} </>}
                  {h.reason && <>· {h.reason}</>}
                </div>
              </div>
            ))}
            {(!feedback.captain_history || feedback.captain_history.length === 0) && (
              <div className="text-xs text-slate-500 text-center py-4">No history yet.</div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setHistoryOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
